import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useChatArtifact } from "../contexts/ChatArtifactContext";
import Sidebar from "./Sidebar";
import { Button } from "../components/ui/button";
import ThemeToggle from "../components/ui/ThemeToggle";
import { Menu } from "lucide-react";
import { cn } from "../lib/utils";
import OnboardingModal from "../components/modals/onboarding-modal/OnboardingModal.jsx";

export default function MainLayout({ children }) {
  const { pathname } = useLocation();
  const isChatHome = pathname === "/";
  const isReports = pathname === "/reports" || pathname.startsWith("/reports/");
  const { artifactPanelOpen } = useChatArtifact();
  const chatPdfSplitOpen = isChatHome && artifactPanelOpen;
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(!!user);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    if (user) {
      setSidebarOpen(true);
    }
  }, [user]);

  // Enable transitions after the initial paint so the first open has no animation
  useEffect(() => {
    if (!user) return;
    const id = requestAnimationFrame(() => setShouldAnimate(true));
    return () => cancelAnimationFrame(id);
  }, [user]);

  useEffect(() => {
    const html = document.documentElement;
    if (isChatHome) {
      html.setAttribute("data-chat-scroll-lock", "");
    } else {
      html.removeAttribute("data-chat-scroll-lock");
    }
    return () => html.removeAttribute("data-chat-scroll-lock");
  }, [isChatHome]);

  return (

    <div
      className={cn(
        "relative flex w-full bg-background text-foreground",
        isChatHome
          ? "flex flex-1 min-h-0 max-h-full overflow-hidden"
          : "min-h-screen"
      )}
    >
      {/* Sidebar */}
      {user && (
        <div
          className={cn(
            // Ensure there's space for the toggle button and avoid content overlap
            "fixed left-0 top-0 z-40 h-full w-64 pt-16 transform border-r border-border bg-sidebar",
            shouldAnimate ? "transition-transform duration-200 ease-in-out" : "",
            sidebarOpen ? "translate-x-0" : "-translate-x-full",
            sidebarOpen ? "lg:translate-x-0" : "lg:-translate-x-full"
          )}
          style={{ width: "var(--sidebar-width,16rem)" }}
        >
          <Sidebar />
        </div>
      )}

      {/* Main Content */}
      <div
        className={cn(
          "flex flex-1 flex-col min-h-0",
          isChatHome ? "overflow-hidden" : "min-h-screen"
        )}
      >
        {user && (
          <Button
            variant="outline"
            size="icon"
            // Always stick to the very top-left of the viewport regardless of sidebar state
            className="fixed left-4 top-4 z-50"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <Menu className="h-6 w-6" />
          </Button>
        )}

        {/* Theme toggle button - always visible */}
        <ThemeToggle className="fixed right-4 top-4 z-50" />
        <main
          className={cn(
            "flex w-full flex-1 flex-col min-h-0",
            isChatHome ? "overflow-hidden" : "overflow-x-hidden",
            shouldAnimate ? "transition-all duration-200 ease-in-out" : "",
            user ? "pt-16" : !isChatHome && "min-h-screen",
            user && sidebarOpen ? "lg:pl-64" : ""
          )}
        >
          <div
            className={cn(
              "w-full min-h-0",
              chatPdfSplitOpen && "flex flex-1 flex-col overflow-hidden px-0 pb-0",
              isChatHome &&
                !chatPdfSplitOpen &&
                "mx-auto flex min-h-0 flex-1 flex-col overflow-hidden max-w-screen-md px-4 pb-2",
              !isChatHome && isReports && "mx-auto h-full w-full max-w-screen-2xl px-4 pb-4",
              !isChatHome && !isReports && "mx-auto h-full max-w-screen-md px-4 pb-4"
            )}
          >
            {children}
          </div>

          
          <div className="fixed bottom-4 right-4 z-50 hidden 2xl:block">
            {<OnboardingModal />}
          </div>
              
            

        </main>
      </div>
    </div>
  );
}
